import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, FormSpy } from 'react-final-form';
import { isEqual } from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import {
  setCreationResetButtonIsDisabled,
  toggleAuthWindow
} from '../../actions';
import { AddNewItemForm } from './AddNewItemForm';
import { userDataQuery, getCategories } from '../../api/queries';
import { createItemMutation } from '../../api/mutations';
import Bugsnag from '../../utils/bugsnag';
import {
  BUILDING_TYPE_ID,
  Cities,
  FORM_VALUES
} from '../../utils/helpers/constants';
import { convertFormToQueryInput } from '../../utils/helpers/convertFormToQueryInput';
import { getCategoryValues, validateFunc } from '../../utils/helpers/common';
import { validatorsConfig } from '../../utils/helpers/validateItemForm';
import { CreateNewItemsProps, CreatePageStepsEnum, ValuesType } from './types';
import { SelectLeasedTypeStep } from './SelectLeasedTypeStep';
import { SelectCategoryStep } from './SelectCategoryStep';
import { SelectOwnerTypeStep } from './SelectOwnerTypeStep';
import { ItemCreateStatusEnum } from '../../graphql/types';
import { ErrorPopup } from '../ErrorPopup';
import {
  CreateItemMutation,
  ItemCreateContactTypeEnum,
  UserDataQuery
} from '../../graphql/graphql';
import { pushButtonNewAdDataLayerEvent } from '../../utils/pushDataLayerEvents';
import { useCreationFormStepFlow } from '../../hooks/useCreationFormStepFlow';

interface ItemCreationFormProps {
  returnURL: string;
}

export const ItemCreationForm: React.FC<ItemCreationFormProps> = ({
  returnURL
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const [createItem] = useMutation(createItemMutation);
  const [errorText, setErrorText] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [failedModalIsOpen, setFailedModalIsOpen] = useState(false);
  const [isBannedModalVisible, setIsBannedModalVisible] = useState(false);

  const validate = validateFunc(t, validatorsConfig);

  const { data: dataCategories, loading: loadingCategories } =
    useQuery(getCategories);
  const categories = loadingCategories ? [] : dataCategories?.categories;
  const typeEstate = (categories || [])?.find(
    category => category.id === BUILDING_TYPE_ID.FLAT_ALL
  )?.name;

  const categoryValues = getCategoryValues({
    id: BUILDING_TYPE_ID.FLAT_ALL,
    categories
  });

  const { data } = useQuery<UserDataQuery>(userDataQuery, {
    ssr: false,
    fetchPolicy: 'network-only'
  });

  const { pageStep, setPageStep, goNext } = useCreationFormStepFlow({
    profile: data?.profile || {}
  });

  const toggleFailedModal = () => setFailedModalIsOpen(!failedModalIsOpen);

  const handleErrors = result => {
    if (result?.errors?.[0]?.message) {
      setErrorText(result.errors[0].message);
    }
    Bugsnag.notify(`Status ${result.status} in create new item`, result);
    toggleFailedModal();
  };

  const createNewItem = ({ accessToken, values }: CreateNewItemsProps) => {
    createItem({
      variables: convertFormToQueryInput(values)
    })
      .then((res: { data: CreateItemMutation }) => {
        const data = res.data?.createItem;
        if (data?.status === ItemCreateStatusEnum.Success) {
          const newItemId = data?.item?.id;
          const accessTokenParam = accessToken
            ? `?access_token=${accessToken}`
            : '';
          const redirectUrl = `/items/${newItemId}${accessTokenParam}`;
          router.push(redirectUrl);
        } else if (
          data?.status === ItemCreateStatusEnum.Failure ||
          data?.status === ItemCreateStatusEnum.Invalid
        ) {
          handleErrors(data);
          setLoading(false);
          dispatch(setCreationResetButtonIsDisabled(false));
        } else if (data?.status === ItemCreateStatusEnum.Restricted) {
          setLoading(false);
          setIsBannedModalVisible(true);
          setDisableSubmitButton(true);
        }
      })
      .catch(error => {
        dispatch(setCreationResetButtonIsDisabled(false));
        handleErrors({ status: 'UNHANDLED', errors: [error] });
        setLoading(false);
      });
  };

  const onSubmit = async (values: ValuesType) => {
    setLoading(true);

    if (data?.profile) {
      createNewItem({ values });
    } else {
      dispatch(toggleAuthWindow(true));
    }
  };

  const initialValues = useMemo(
    () => ({
      [FORM_VALUES.cityId]: Cities.Baku.id,
      [FORM_VALUES.owner]: ItemCreateContactTypeEnum.Owner,
      [FORM_VALUES.hasMortgage]: false,
      [FORM_VALUES.hasBillOfSale]: false,
      [FORM_VALUES.categoryId]: BUILDING_TYPE_ID.FLAT_NEW,
      [FORM_VALUES.typeEstate]: typeEstate,
      [FORM_VALUES.categoryValues]: categoryValues,
      [FORM_VALUES.leased]: undefined,
      [FORM_VALUES.cityHasLocations]: true
    }),
    []
  );

  const changeFormHandler = useCallback(
    ({ values }: { values: ValuesType }) => {
      const profileValues = data?.profile
        ? {
            [FORM_VALUES.email]: data.profile.email,
            [FORM_VALUES.name]: data.profile.name,
            [FORM_VALUES.phone]: (data.profile.phone || '').replace(
              /[^\d]/g,
              ''
            )
          }
        : {};

      const initialFormValues = {
        ...initialValues,
        ...profileValues
      };

      const isValuesEqual = isEqual(initialFormValues, values);
      setTimeout(
        () => dispatch(setCreationResetButtonIsDisabled(isValuesEqual)),
        0
      );
    },
    [data?.profile, initialValues, dispatch]
  );

  useEffect(() => {
    pushButtonNewAdDataLayerEvent();
    const handleRouteChange = () => {
      setPageStep(CreatePageStepsEnum.DealType);
      window.scrollTo(0, 0);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if (data?.profile?.isRestricted) {
      setIsBannedModalVisible(true);
      setDisableSubmitButton(true);
    }
  }, [data?.profile?.isRestricted]);

  const handleClosePopup = () => {
    window.location.href = `${window.location.origin}/`;
    setIsBannedModalVisible(false);
  };

  return (
    <>
      <Form
        onSubmit={onSubmit}
        validate={validate}
        subscription={{
          submitting: true,
          values: true
        }}
        initialValues={initialValues}
      >
        {renderProps => (
          <form
            id="new_ad"
            method="get"
            acceptCharset="UTF-8"
            onSubmit={renderProps.handleSubmit}
          >
            <FormSpy
              onChange={changeFormHandler}
              subscription={{ values: true }}
            />
            {pageStep === CreatePageStepsEnum.DealType && (
              <SelectLeasedTypeStep goNext={goNext} />
            )}
            {pageStep === CreatePageStepsEnum.PropertyType && (
              <SelectCategoryStep goNext={goNext} categories={categories} />
            )}
            {pageStep === CreatePageStepsEnum.OwnerType && (
              <SelectOwnerTypeStep goNext={goNext} />
            )}
            {pageStep === CreatePageStepsEnum.Form && (
              <AddNewItemForm
                profile={data?.profile}
                errorText={errorText}
                categories={categories}
                setErrorText={setErrorText}
                createNewItem={createNewItem}
                toggleFailedModal={toggleFailedModal}
                failedModalIsOpen={failedModalIsOpen}
                returnToURL={returnURL}
                loading={loading}
                setLoading={setLoading}
                disableSubmitButton={disableSubmitButton}
                setPageStep={setPageStep}
              />
            )}
          </form>
        )}
      </Form>
      <ErrorPopup
        isOpen={isBannedModalVisible}
        onClose={handleClosePopup}
        isBannedPhone
      />
    </>
  );
};
